/**
 * Athleten-Pop-up: Headshot, Name, Startnummer, Team, ein Satz zur Person
 * und ihre Netzwerke -- fuenf Sekunden lang in einer Ecke des Hauptbilds.
 *
 * Gepflegt wird alles in der Startliste der Regie; die Daten haengen an der
 * Person und damit an ihrer Startnummer. Ein Abgleich mit RACE RESULT laesst
 * sie stehen, solange die Nummer dort weiter vorkommt.
 *
 * Ausgeloest wird per WebSocket (`spotlight:show` mit der Startnummer). Der
 * Server baut daraus einen fertigen Schnappschuss (`AthleteSpotlight`) --
 * Zuschauerseite und Renderseite brauchen dafuer keine Startliste -- und
 * nimmt ihn nach `SPOTLIGHT_MS` von selbst wieder weg.
 */
import type { CornerSlot, WidgetSize } from './ticker.ts';

/* ------------------------------------------------------------ Netzwerke */

export type SocialId = 'instagram' | 'tiktok' | 'strava' | 'youtube' | 'facebook' | 'x' | 'website';

export const SOCIALS: { id: SocialId; label: string; placeholder: string; handle: boolean }[] = [
  { id: 'instagram', label: 'Instagram', placeholder: '@name oder Link', handle: true },
  { id: 'tiktok', label: 'TikTok', placeholder: '@name oder Link', handle: true },
  { id: 'strava', label: 'Strava', placeholder: 'Name oder Link', handle: false },
  { id: 'youtube', label: 'YouTube', placeholder: '@kanal oder Link', handle: true },
  { id: 'facebook', label: 'Facebook', placeholder: 'Name oder Link', handle: false },
  { id: 'x', label: 'X', placeholder: '@name oder Link', handle: true },
  { id: 'website', label: 'Webseite', placeholder: 'beispiel.de', handle: false },
];

/** Je Netzwerk ein Eintrag, so wie die Regie ihn getippt hat -- Name oder Link. */
export type ParticipantSocials = Partial<Record<SocialId, string>>;

export const MAX_SOCIAL_LENGTH = 120;
/** Ein Satz zur Person -- im Pop-up eine Zeile. */
export const MAX_PARTICIPANT_INFO = 80;
export const MAX_PARTICIPANT_TEAM = 60;

/** Nur bekannte Netzwerke, getrimmt und gekuerzt; leere fallen weg. */
export function cleanSocials(raw: unknown): ParticipantSocials {
  const input = (raw ?? {}) as Record<string, unknown>;
  const out: ParticipantSocials = {};
  for (const { id } of SOCIALS) {
    const value = input[id];
    if (typeof value !== 'string') continue;
    const text = value.replace(/\s+/g, ' ').trim().slice(0, MAX_SOCIAL_LENGTH);
    if (text) out[id] = text;
  }
  return out;
}

const looksLikeUrl = (value: string) => /^https?:\/\//i.test(value) || /^(www\.)?[\w-]+(\.[\w-]+)+\//i.test(value);

/**
 * Was im Pop-up neben dem Symbol steht: aus einem Link das, was man sich
 * merken kann (`instagram.com/anna.runs` → `@anna.runs`), aus einem Namen
 * bei Instagram, TikTok, YouTube und X der Name mit @ davor. Ein Link, an
 * dessen Ende nur eine Nummer steht (Strava, Facebook), wird zu „Profil" --
 * eine Zahl sagt im Bild niemandem etwas.
 */
export function socialLabel(id: SocialId, value: string): string {
  const text = value.trim();
  const handle = SOCIALS.find((s) => s.id === id)?.handle ?? false;
  if (id === 'website') {
    return text.replace(/^https?:\/\//i, '').replace(/^www\./i, '').replace(/\/+$/, '');
  }
  let name = text;
  if (looksLikeUrl(text)) {
    try {
      const url = new URL(/^https?:\/\//i.test(text) ? text : `https://${text}`);
      const parts = url.pathname.split('/').filter(Boolean);
      name = decodeURIComponent(parts.at(-1) ?? '');
    } catch {
      name = '';
    }
    if (!name || /^\d+$/.test(name) || /\.php$/i.test(name)) return 'Profil';
  }
  name = name.replace(/^@+/, '');
  return handle ? `@${name}` : name;
}

/* ------------------------------------------------------------- Einstellung */

/** Wie lange ein Pop-up steht. */
export const SPOTLIGHT_MS = 5000;

/** Wo und wie gross das Pop-up steht -- eine Einstellung fuer alle. */
export interface SpotlightConfig {
  slot: CornerSlot;
  size: WidgetSize;
  /** Akzentfarbe als `#rrggbb`, ohne Angabe das Rot der Einblendungen. */
  color?: string;
  /** Durchgang: wer drankommt -- ohne Angabe alle aus der Startliste. */
  tourWho?: SpotlightTourWho;
  /** Durchgang: auch wer weder Foto noch Netzwerk hat. */
  tourEmpty?: boolean;
  /** Durchgang: Sekunden von Pop-up zu Pop-up, 0 = direkt nacheinander. */
  tourGap?: number;
}

export const defaultSpotlightConfig = (): SpotlightConfig => ({ slot: 'bottomLeft', size: 'md' });

/* -------------------------------------------------------------- Durchgang */

/*
 * Statt jede Person einzeln anzuklicken, laesst die Regie die Pop-ups
 * nacheinander laufen: alle aus der Startliste oder nur, wer noch im Rennen
 * ist, nach Startnummer. Der Server fuehrt den Durchgang -- er laeuft weiter,
 * wenn die Regie den Tab schliesst, und wartet, solange eine Werbepause,
 * eine Botschaft oder ein Pop-up von Hand im Bild ist.
 */

export type SpotlightTourWho = 'all' | 'racing';

export const SPOTLIGHT_TOUR_WHO: { id: SpotlightTourWho; label: string; hint: string }[] = [
  { id: 'all', label: 'Alle', hint: 'Die ganze Startliste' },
  { id: 'racing', label: 'Im Rennen', hint: 'Nur wer auf der Strecke als „unterwegs“ steht' },
];

/** Abstand von Start zu Start. „Direkt" heisst: Pop-up, kurz Luft, das naechste. */
export const SPOTLIGHT_TOUR_GAPS: { seconds: number; label: string }[] = [
  { seconds: 0, label: 'direkt nacheinander' },
  { seconds: 15, label: 'alle 15 s' },
  { seconds: 30, label: 'alle 30 s' },
  { seconds: 60, label: 'jede Minute' },
  { seconds: 300, label: 'alle 5 min' },
];

/** Luft zwischen zwei Pop-ups, wenn sie direkt nacheinander kommen. */
export const SPOTLIGHT_TOUR_BREATH_MS = 1000;

export const tourStepMs = (gapSeconds: number): number =>
  Math.max(SPOTLIGHT_MS + SPOTLIGHT_TOUR_BREATH_MS, gapSeconds * 1000);

/** Einstellung des Durchgangs mit allen Vorgaben aufgefuellt. */
export function tourSettings(config: SpotlightConfig): { who: SpotlightTourWho; empty: boolean; gap: number } {
  return { who: config.tourWho ?? 'all', empty: config.tourEmpty ?? false, gap: config.tourGap ?? 0 };
}

/** Ob eine Person etwas fuers Pop-up hat -- ein Foto oder ein Netzwerk. */
export const hasSpotlightProfile = (p: { photoUpdatedAt?: number; socials?: ParticipantSocials }): boolean =>
  Boolean(p.photoUpdatedAt) || Object.values(p.socials ?? {}).some(Boolean);

/** Ein laufender Durchgang, so wie die Regie ihn sieht. */
export interface SpotlightTour {
  who: SpotlightTourWho;
  empty: boolean;
  gapSeconds: number;
  /** So viele standen beim Start auf der Liste. */
  total: number;
  /** Die wievielte gerade dran ist bzw. zuletzt dran war; 0 vor der ersten. */
  position: number;
  /** Wer gerade dran ist bzw. zuletzt dran war. */
  current: { bib: string; name: string } | null;
  /** Wann die naechste Person kommt (ms seit 1970) -- null bei der letzten und beim Warten. */
  nextAt: number | null;
  /** Wartet, bis Werbepause, Botschaft oder ein Pop-up von Hand vorbei ist. */
  waiting: 'break' | 'manual' | null;
}

/* ---------------------------------------------------------------- Zustand */

/** Ein laufendes Pop-up -- alles, was es zeigt, ohne Startliste dahinter. */
export interface AthleteSpotlight {
  /** Neu je Ausloesen: daran erkennt die Seite ein neues Pop-up derselben Person. */
  id: string;
  bib: string;
  name: string;
  team: string;
  info: string;
  photoUrl: string | null;
  socials: { id: SocialId; label: string }[];
}

export interface SpotlightState {
  active: AthleteSpotlight | null;
  config: SpotlightConfig;
  /** Laufender Durchgang, sonst null. */
  tour: SpotlightTour | null;
}

export const emptySpotlight = (): SpotlightState => ({ active: null, config: defaultSpotlightConfig(), tour: null });

/** Adresse des Headshots -- mit Zeitstempel, damit ein neues Foto sofort gilt. */
export const headshotUrl = (slug: string, participantId: string, version?: number): string | null =>
  version ? `/api/events/${slug}/headshots/${participantId}?v=${version}` : null;
